import { createSelector } from '@ngrx/store';
import {
  AlertLifecycleStage,
  AlertTarget,
  AlertUrgency,
  EntityType,
  RuleMatchType,
  VisibilityType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import {
  ActiveAlertRecord,
  selectActiveAlertEntitiesByEntityType,
} from 'src/store/generated/active-alert-record.generated';
import { selectAllActiveAlertAcknowledgementEntities } from '../generated/active-alert-acknowledgement-record.generated';

const urgencySortOrder: AlertUrgency[] = [
  AlertUrgency.Critical,
  AlertUrgency.Warning,
  AlertUrgency.Information,
];

const lifecycleStageSortOrder: AlertLifecycleStage[] = [
  AlertLifecycleStage.PreAssignment, // flash
  AlertLifecycleStage.Unspecified,
];

export const selectAllActiveAlerts = (appState: AppState) =>
  appState.entityStores.activeAlert.entities;

export function selectActiveAlertById(id: string) {
  return createSelector(selectAllActiveAlerts, (reasons) => reasons.get(id));
}

export function selectActiveAlertsByCardId(cardId: string) {
  return createSelector(selectActiveAlertEntitiesByEntityType(EntityType.Card), (alerts) =>
    alerts
      .valueSeq()
      .filter((a) => a.entityId === cardId && a.visibilityType !== VisibilityType.TargetOnly)
      .sortBy((a) => a.urgency),
  );
}

export function selectActiveAlertsByCardIdByMatchType(cardId: string, matchType: RuleMatchType) {
  return createSelector(selectActiveAlertsByCardId(cardId), (alerts) => {
    return alerts
      .valueSeq()
      .filter((aa) => aa.matchType === matchType)
      .sortBy((aa) => urgencySortOrder.findIndex((x) => x === aa.urgency));
  });
}

export function selectUnacknowledgedActiveAlertsByCardId(cardId: string) {
  return createSelector(selectActiveAlertsByCardId(cardId), (alerts) => {
    return alerts
      .toIndexedSeq()
      .filter((aa) => aa.requiresAcknowledgement && aa.acknowledgedById == null)
      .sortBy(sortActiveAlerts);
  });
}

export function selectAcknowledgedActiveAlertsByCardId(cardId: string) {
  return createSelector(selectActiveAlertsByCardId(cardId), (alerts) => {
    return alerts
      .toIndexedSeq()
      .filter((aa) => aa.requiresAcknowledgement && aa.acknowledgedById != null)
      .sortBy(sortActiveAlerts);
  });
}

export function selectOtherActiveAlertsByCardId(cardId: string) {
  return createSelector(
    selectActiveAlertsByCardId(cardId),
    selectUnacknowledgedActiveAlertsByCardId(cardId),
    selectAcknowledgedActiveAlertsByCardId(cardId),
    (allAlerts, unacknowledgedAlerts, acknowledgedAlerts) => {
      const excludeAlerts = [...unacknowledgedAlerts.toArray(), ...acknowledgedAlerts.toArray()];
      return (
        allAlerts
          .toIndexedSeq()
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          .filter((alert) => !excludeAlerts.includes(alert))
          .sortBy(sortActiveAlerts)
      );
    },
  );
}

export function selectAllUnacknowledgedActiveAlertsByCompanyId(
  companyId: string,
  entityType?: EntityType,
) {
  return createSelector(selectAllActiveAlerts, (alerts) => {
    return alerts
      .toIndexedSeq()
      .filter((aa) => aa.companyId === companyId)
      .filter((aa) => {
        const now = new Date();
        const isEntityType = entityType == null ? true : entityType === aa.entityType;
        const isUnacked = aa.requiresAcknowledgement && aa.acknowledgedById == null;
        const isActive = aa.activationTime == null ? true : aa.activationTime <= now;
        const isNotTargetOnly = aa.visibilityType !== VisibilityType.TargetOnly;
        return isUnacked && isActive && isEntityType && isNotTargetOnly;
      })
      .sortBy(sortActiveAlerts);
  });
}

export function sortActiveAlerts(aa: ActiveAlertRecord) {
  return (
    (aa.requiresAcknowledgement ? 1 : 0) +
    urgencySortOrder.findIndex((x) => x === aa.urgency) +
    lifecycleStageSortOrder.findIndex((x) => x === aa.lifecycleStage)
  );
}

export function selectUnacknowledgedFleetStatusActiveAlertsByByUserIdAndCardId(
  userId: string,
  cardId: string,
  lastTenMinutesOnly?: boolean,
) {
  return createSelector(
    selectAllActiveAlerts,
    selectAllActiveAlertAcknowledgementEntities,
    (alerts, acknowledgments) => {
      return alerts
        .toIndexedSeq()
        .filter((aa) => {
          const now = new Date();
          const isUnacknowledged =
            acknowledgments
              .valueSeq()
              .toArray()
              .find((ack) => ack.acknowledgedById === userId && ack.activeAlertId === aa.id) != null
              ? false
              : true;
          const cardIsIncluded = aa.entityId === cardId;
          const activationTime = aa.activationTime ?? aa.dateCreated;
          const validActivationTime = aa.activationTime == null ? true : aa.activationTime <= now;
          const isActive =
            lastTenMinutesOnly === true
              ? activationTime >= new Date(Date.now() - 10 * 60 * 1000) && activationTime <= now
              : validActivationTime;
          const isFleetStatusAlert = aa.target === AlertTarget.FleetStatusViewer;
          return isFleetStatusAlert && cardIsIncluded && isActive && isUnacknowledged;
        })
        .sortBy(sortActiveAlerts);
    },
  );
}

export function selectUnacknowledgedFleetStatusActiveAlertsByUserIdAndCardIds(
  userId: string,
  cardIds: string[],
  lastTenMinutesOnly?: boolean,
) {
  return createSelector(
    selectAllActiveAlerts,
    selectAllActiveAlertAcknowledgementEntities,
    (alerts, acknowledgments) => {
      return alerts
        .toIndexedSeq()
        .filter((aa) => {
          if (aa.target === AlertTarget.FleetStatusViewer) {
            const now = new Date();
            const cardIsIncluded = aa.entityId != null ? cardIds.includes(aa.entityId) : false;
            const isUnacknowledged =
              acknowledgments
                .valueSeq()
                .toArray()
                .find((ack) => ack.acknowledgedById === userId && ack.activeAlertId === aa.id) !=
              null
                ? false
                : true;
            const activationTime = aa.activationTime ?? aa.dateCreated;
            const validActivationTime = aa.activationTime == null ? true : aa.activationTime <= now;
            const isActive =
              lastTenMinutesOnly === true
                ? activationTime >= new Date(Date.now() - 10 * 60 * 1000) && activationTime <= now
                : validActivationTime;
            return cardIsIncluded && isUnacknowledged && isActive;
          } else {
            return false;
          }
        })
        .sortBy(sortActiveAlerts);
    },
  );
}
